import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";

import "@/lib/i18n";
import "@/styles/index.css";

import { requestPersistentStorage } from "@/repositories/storageRepository";
import { connectAuthToApiClient, useAuthStore } from "@/stores/authStore";
import { useSyncStore } from "@/stores/syncStore";

import { Providers } from "./app/providers";
import { reloadForNewVersion } from "./app/staleVersion";
import { router } from "./app/router";

// Wire the API client to the auth store before anything can issue a request.
// Done here rather than inside either module so neither has to import the other.
connectAuthToApiClient();

// Rehydrate from IndexedDB. Not awaited: the guards render a spinner while the
// status is "loading", so blocking the first paint would only delay it.
void useAuthStore.getState().restore();

// The sync loop runs from boot, not only inside the signed-in shell. A shop
// that closes with sales still queued has nobody signed in, and its queue must
// still go up on the till's own credential the moment there is signal.
useSyncStore.getState().start();

// Ask the browser not to evict this origin's data. IndexedDB is "best effort"
// by default, and on this terminal it holds sales that exist nowhere else in the
// world until they upload — a browser reclaiming space on a full tablet would
// take a shop's Saturday with it. Not awaited: a permission prompt must never
// sit between a cashier and the till.
void requestPersistentStorage();

// A release replaces the files screens load on demand, and a page opened before
// it asks for ones that are gone. Vite reports that here before any screen
// fails; reloading once fetches the new version. See `RouteError`.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadForNewVersion()) event.preventDefault();
});

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element #root is missing from index.html.");
}

createRoot(container).render(
  <StrictMode>
    <Providers>
      <RouterProvider router={router} />
    </Providers>
  </StrictMode>,
);
